import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.jsx';
import { useSwipeGesture } from '../discover/useSwipeGesture.js';
import { color, font, radius } from '../../theme/tokens.js';
import { GroupReveal } from './GroupReveal.jsx';
import { useTypewriter } from './useTypewriter.js';
import { VenueCard } from './VenueCard.jsx';
import { venues } from './venues.generated.js';

/** The warm off-white the whole page is written in. */
const CREAM = '#F7F4ED';

/** A red that survives being printed on the brand green. `color.danger` does not. */
const SOFT_RED = '#FFB4A6';

/**
 * The things people say instead of deciding.
 *
 * Typed out one at a time, and answered by the card underneath. Naming the
 * problem in the visitor's own words does more than a sentence about food
 * discovery would, and it is shorter.
 *
 * Stored without their quotation marks. The marks are drawn either side of the
 * typing and never erased, so the line always reads as a complete quoted
 * sentence with the words inside it changing — rather than as a closing quote
 * that pops back into existence each time the typing happens to finish.
 */
const EXCUSES = ['you pick.', 'i don’t care.', 'anywhere’s fine.', 'wherever.'];

/**
 * Which way the reel swipes when it is playing itself.
 *
 * Weighted towards yes, because the point being demonstrated is that a decision
 * gets reached — a reel of four rejections argues the opposite.
 *
 * @type {Array<'left' | 'right'>}
 */
const SCRIPT = ['right', 'left', 'right', 'right'];

/** How many cards are drawn. Three is enough to read as a stack. */
const VISIBLE = 3;

/** The public preview should not feel like an endless web app deck. */
const DEMO_SWIPE_LIMIT = venues.length;

/** Long enough to finish typing the first line and let it sit. */
const FIRST_MS = 3400;

/** The gap between swipes after that. */
const STEP_MS = 3000;

/** How long the reel stays hands-off after someone takes over. */
const RESUME_MS = 7000;

/**
 * The landing page's one moving part, and the only thing on it that argues.
 *
 * The problem types itself out in the visitor's own words. The answer is
 * already on the card underneath. Then the card is actually decided on —
 * stamped and thrown, by the buttons or by a finger — and the next line begins.
 *
 * That middle beat is the whole point. An earlier version advanced by swapping
 * the top card out from under the stack, which showed photographs and no
 * product: nothing was ever chosen, so nothing was ever solved.
 *
 * A yes made *by hand* opens {@link GroupReveal} — the second half of the
 * product, which only means anything once someone has actually decided
 * something. The timer's own swipes never open it: a panel that appears every
 * three seconds unprompted is an advert, not a reply.
 */
export function DecisionReel() {
  const [index, setIndex] = useState(0);
  const [trigger, setTrigger] = useState(/** @type {'left'|'right'|null} */ (null));
  const [pausedAt, setPausedAt] = useState(/** @type {number|null} */ (null));
  const [revealed, setRevealed] = useState(/** @type {string|null} */ (null));
  const stillMotion = usePrefersReducedMotion();

  // Whether the swipe now in flight was started by a person. A ref rather than
  // state because nothing renders differently for it — it only has to survive
  // until the card lands, which is a later tick than the one that set it.
  const byHand = useRef(false);

  const paused = pausedAt !== null;
  const capped = index >= DEMO_SWIPE_LIMIT;
  const typed = useTypewriter(EXCUSES[index % EXCUSES.length], !stillMotion);

  // Someone who has asked for less motion gets the first line typed out whole
  // and a deck that sits still. A stylesheet can flatten a CSS animation but not
  // a timer, so this is the only place that setting can be honoured.
  useEffect(() => {
    if (paused || stillMotion || revealed || capped) return;

    const timer = window.setTimeout(
      () => {
        // Cleared here as well as on commit: a drag that sprang back instead of
        // committing leaves this set, and the next automatic swipe would then
        // be mistaken for the visitor's own.
        byHand.current = false;
        setTrigger(SCRIPT[index % SCRIPT.length]);
      },
      index === 0 ? FIRST_MS : STEP_MS
    );

    return () => window.clearTimeout(timer);
  }, [index, paused, stillMotion, revealed, capped]);

  // Restarts a while after the last touch. Keyed on `pausedAt` so every fresh
  // interaction pushes the restart back rather than stacking up.
  useEffect(() => {
    if (!paused) return;
    const timer = window.setTimeout(() => setPausedAt(null), RESUME_MS);
    return () => window.clearTimeout(timer);
  }, [pausedAt, paused]);

  /**
   * Runs once the flying card has left, whether a timer or a person threw it.
   *
   * @param {'left'|'right'} direction
   */
  function commit(direction) {
    const deliberate = byHand.current;
    byHand.current = false;

    if (deliberate && direction === 'right') setRevealed(venues[index % venues.length].name);

    // Cleared before the index moves so the next card mounts with no pending
    // trigger — otherwise it would leave the instant it arrived.
    setTrigger(null);
    setIndex((n) => n + 1);
  }

  function takeOver() {
    byHand.current = true;
    setPausedAt(Date.now());
  }

  /** @param {'left'|'right'} direction */
  function press(direction) {
    if (capped) return;
    takeOver();
    setTrigger(direction);
  }

  function replay() {
    byHand.current = false;
    setTrigger(null);
    setPausedAt(null);
    setRevealed(null);
    setIndex(0);
  }

  return (
    <div style={styles.reel}>
      {/* One steady sentence for a screen reader. The visible version rewrites
          itself several times a minute, which is worth watching and miserable
          to listen to. */}
      <p style={styles.srOnly}>
        Crave ends the “where do you want to eat?” conversation. It shows you real restaurants near
        you, one at a time, and you swipe to decide. Start a group and everyone votes from their own
        phone.
      </p>

      <div style={styles.stage}>
        {revealed ? (
          <GroupReveal venueName={revealed} onDismiss={() => setRevealed(null)} />
        ) : capped ? (
          <PreviewComplete onReplay={replay} />
        ) : (
          <>
            <div aria-hidden="true">
              <p style={styles.lead}>No more</p>
              <p style={styles.typed}>
                <span style={styles.quote}>“</span>
                {typed}
                <span className={stillMotion ? undefined : 'caret'} style={styles.caret}>
                  {' '}
                </span>
                <span style={styles.quote}>”</span>
              </p>
            </div>

            <div style={styles.stack} aria-hidden="true">
              {Array.from({ length: VISIBLE }, (_, slot) => {
                const at = index + slot;
                return (
                  <ReelCard
                    key={at}
                    venue={venues[at % venues.length]}
                    stackIndex={slot}
                    trigger={slot === 0 ? trigger : null}
                    onCommit={commit}
                    onInteract={takeOver}
                  />
                );
              })}
            </div>

            {/* The mechanism, spelled out. Two buttons is the entire interface
                the app asks anyone to learn, so showing them here is both the
                honest preview and the shortest explanation of how this works. */}
            <div style={styles.answer}>
              <div style={styles.actions}>
                <ActionButton
                  label="Pass"
                  tone={SOFT_RED}
                  icon="close"
                  onClick={() => press('left')}
                />
                <ActionButton label="Dine" tone={CREAM} filled onClick={() => press('right')} />
              </div>

              <p className="reel-closer" style={styles.closer}>
                One swipe.
                <span style={styles.closerStrong}> Dinner’s decided.</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewComplete({ onReplay }) {
  return (
    <div style={styles.complete} className="fade-in">
      <div style={styles.completeIcon} aria-hidden="true">
        <Icon name="check" size={22} stroke={color.primaryDark} strokeWidth={3} />
      </div>
      <p style={styles.completeTitle}>That’s today’s preview</p>
      <p style={styles.completeBody}>
        Crave keeps the real deck focused so choosing dinner does not turn into another scroll.
      </p>
      <button type="button" onClick={onReplay} style={styles.replayButton}>
        Replay the deck
      </button>
    </div>
  );
}

/**
 * One of the two round buttons under the deck.
 *
 * Real buttons, not a picture of them: pressing one throws the card and stops
 * the reel playing itself. Someone who tries it has used the product.
 *
 * @param {object} props
 * @param {string} props.label  Also the accessible name — the button is icon-only.
 * @param {string} props.tone
 * @param {boolean} [props.filled]  The affirmative one, weighted to read first.
 * @param {'close'|'check'} [props.icon]
 * @param {() => void} props.onClick
 */
function ActionButton({ label, tone, filled = false, icon = 'check', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        ...styles.action,
        background: filled ? tone : 'transparent',
        border: `1.5px solid ${filled ? tone : 'rgba(247,244,237,0.42)'}`,
      }}
    >
      <Icon
        name={icon}
        size={20}
        stroke={filled ? color.primaryDark : tone}
        strokeWidth={filled ? 3 : 2.4}
      />
    </button>
  );
}

/** Scale, offset and fade for each visible position in the stack. */
const STACK_SCALE = [1, 0.945, 0.89];
const STACK_OFFSET_Y = [0, 12, 23];
const STACK_FADE = [1, 0.5, 0.22];

/**
 * A {@link VenueCard} with the app's own swipe gesture on it.
 *
 * @param {object} props
 * @param {(typeof import('./venues.generated.js').venues)[number]} props.venue
 * @param {number} props.stackIndex  0 is the top, front-most card.
 * @param {'left'|'right'|null} props.trigger  Set by the timer or the buttons.
 * @param {(direction: 'left'|'right') => void} props.onCommit
 * @param {() => void} props.onInteract
 */
function ReelCard({ venue, stackIndex, trigger, onCommit, onInteract }) {
  const isTop = stackIndex === 0;

  const { offsetX, flyDirection, isDragging, fly, handlers } = useSwipeGesture({
    enabled: isTop,
    onCommit,
  });

  // The `flyDirection` guard matters: pressing the other button while a card is
  // already on its way out would start a second flight, and each one schedules
  // its own commit — the reel would jump two cards for one press.
  useEffect(() => {
    if (trigger && isTop && !flyDirection) fly(trigger);
  }, [trigger, isTop, flyDirection, fly]);

  // A verdict shows whether the card was thrown by a finger or by the timer,
  // which is what makes an automatic swipe read as a decision rather than as a
  // slideshow advancing.
  const yes = flyDirection === 'right' ? 1 : Math.min(Math.max(offsetX, 0) / 70, 1);
  const no = flyDirection === 'left' ? 1 : Math.min(Math.max(-offsetX, 0) / 70, 1);

  return (
    <div
      onMouseDown={(event) => {
        onInteract();
        handlers.onMouseDown(event);
      }}
      onTouchStart={(event) => {
        onInteract();
        handlers.onTouchStart(event);
      }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 30 - stackIndex,
        transformOrigin: 'bottom center',
        // Tells the browser this element owns the gesture, so iOS doesn't try
        // to read a diagonal drag as a scroll at the same time.
        touchAction: 'none',
        userSelect: 'none',
        cursor: isTop ? (isDragging ? 'grabbing' : 'grab') : 'default',
        ...placement({ stackIndex, isTop, offsetX, flyDirection, isDragging }),
      }}
    >
      {/* The cards behind peek out at the foot, which is exactly where the name
          sits — so their captions stay hidden until they reach the top, or a
          second restaurant's details print underneath the first. */}
      <VenueCard venue={venue} infoOpacity={isTop ? 1 : 0}>
        <Stamp label="DINE" tone={CREAM} opacity={isTop ? yes : 0} side="left" />
        <Stamp label="PASS" tone={SOFT_RED} opacity={isTop ? no : 0} side="right" />
      </VenueCard>
    </div>
  );
}

/**
 * The rotated verdict printed across a card as it goes.
 *
 * `DINE` rather than anything more explanatory: it is what the iOS app stamps,
 * it is the same length as `PASS` so the pair reads as a pair, and a longer
 * word runs off the side of the card while the card is mid-throw.
 */
function Stamp({ label, tone, opacity, side }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 18,
        [side]: 16,
        zIndex: 12,
        opacity,
        padding: '4px 12px',
        border: `2.5px solid ${tone}`,
        borderRadius: 9,
        color: tone,
        fontSize: 15,
        fontWeight: font.weight.black,
        letterSpacing: 1.6,
        textShadow: '0 1px 12px rgba(0,0,0,0.5)',
        transform: `rotate(${side === 'left' ? -13 : 13}deg)`,
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  );
}

/**
 * Where a card sits, and whether it animates getting there.
 *
 * Transitions are off mid-drag so the card tracks the finger exactly; a
 * transition there feels like lag rather than smoothing.
 */
function placement({ stackIndex, isTop, offsetX, flyDirection, isDragging }) {
  if (flyDirection) {
    const sign = flyDirection === 'right' ? 1 : -1;
    return {
      transform: `translateX(${sign * 520}px) rotate(${sign * 16}deg)`,
      opacity: 0,
      transition: 'transform 0.36s ease-in, opacity 0.36s ease-in',
    };
  }

  if (isTop) {
    return {
      transform: `translateX(${offsetX}px) rotate(${offsetX * 0.035}deg)`,
      opacity: 1,
      transition: isDragging
        ? 'none'
        : 'transform 0.34s cubic-bezier(0.2, 0.9, 0.3, 1.1), opacity 0.34s ease',
    };
  }

  return {
    transform: `translateY(${STACK_OFFSET_Y[stackIndex] ?? 23}px) scale(${STACK_SCALE[stackIndex] ?? 0.89})`,
    opacity: STACK_FADE[stackIndex] ?? 0.22,
    transition: 'transform 0.4s cubic-bezier(0.2, 0.9, 0.3, 1.1), opacity 0.4s ease',
  };
}

/**
 * Whether the visitor has asked their system for reduced motion.
 *
 * The global stylesheet already flattens CSS animations under that setting, but
 * this reel moves itself on a timer, which no stylesheet can switch off.
 */
function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (event) => setReduced(event.matches);
    media.addEventListener('change', onChange);
    setReduced(media.matches);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

const styles = {
  // `position: relative` with an explicit z-index makes this a stacking
  // context, which fences in the z-index the buttons need to clear the cards'
  // shadow. Without the fence that 40 is measured against the whole page: on a
  // short screen the reel overflows, and the buttons — being higher than
  // anything in the footer — landed on top of the phone field and swallowed
  // taps meant for it. The field looked fine and simply would not focus.
  reel: { position: 'relative', zIndex: 0, width: '100%', height: '100%' },

  stage: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },

  lead: {
    margin: 0,
    textAlign: 'center',
    fontSize: 15,
    fontWeight: font.weight.semibold,
    color: 'rgba(244,241,234,0.78)',
  },

  typed: {
    textAlign: 'center',
    // Two lines' worth of room, reserved. The longer excuses wrap on a narrow
    // phone, and letting the line grow would shunt the card down mid-word.
    minHeight: '2.05em',
    margin: '4px 0 20px',
    fontSize: 'clamp(30px, 8.6vw, 46px)',
    lineHeight: 1.02,
    fontWeight: font.weight.black,
    letterSpacing: '-1.4px',
    color: CREAM,
  },

  // A block caret rather than a glyph, so it keeps its width whatever font
  // ends up rendering the line.
  // Held open around whatever is being said. The words type and erase between
  // them; the marks themselves never leave the sentence, which is what makes
  // the line read as one person speaking rather than as a string being rebuilt.
  // Quieter than the words, because they are the frame and not the point.
  quote: { color: 'rgba(247,244,237,0.60)' },

  caret: {
    display: 'inline-block',
    width: '0.34em',
    height: '0.78em',
    marginLeft: '0.06em',
    verticalAlign: '-0.04em',
    background: CREAM,
    borderRadius: 2,
  },

  // Takes whatever height is left. The floor keeps the photo readable on a
  // short screen; the ceiling stops one card filling a desktop window.
  // Portrait, like the card in the app. `flex: 1` sets the height from whatever
  // the column has left, and `aspect-ratio` derives the width from it — which
  // needs `align-self: center`, or the flex column would stretch it back to
  // full width and the ratio would have nothing to do.
  stack: {
    position: 'relative',
    flex: 1,
    minHeight: 150,
    maxHeight: 400,
    aspectRatio: '3 / 4',
    alignSelf: 'center',
    // The width bounds are what stop the ratio from being obeyed too
    // literally. On a short screen there is very little height to derive a
    // width from, and 3:4 of not much is a postage stamp — so the card gives up
    // the ratio before it gives up being legible, and goes wide instead. The
    // ceiling does the same job at the other end, where a tall window would
    // otherwise let one card grow past the size of a phone.
    minWidth: 'min(100%, 250px)',
    maxWidth: 'min(100%, 300px)',
  },

  // Above the deck, not below it. The cards carry a 34px drop shadow, and a gap
  // smaller than that left the cream button visibly two-tone. It also means a
  // thrown card passes behind the controls, which is right.
  answer: { position: 'relative', zIndex: 40, flexShrink: 0, paddingTop: 30 },

  actions: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 },
  action: {
    width: 46,
    height: 46,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.pill,
    cursor: 'pointer',
    padding: 0,
  },

  closer: {
    margin: '14px 0 0',
    textAlign: 'center',
    fontSize: 15,
    lineHeight: 1.3,
    fontWeight: font.weight.semibold,
    letterSpacing: '-0.1px',
    color: 'rgba(244,241,234,0.72)',
  },
  // The half that answers the quote above gets the weight; the setup does not.
  closerStrong: { color: CREAM, fontWeight: font.weight.heavy },

  complete: {
    minHeight: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 18px',
  },

  completeIcon: {
    width: 54,
    height: 54,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.pill,
    background: CREAM,
    marginBottom: 16,
  },

  completeTitle: {
    margin: 0,
    color: CREAM,
    fontSize: 28,
    lineHeight: 1.05,
    fontWeight: font.weight.black,
  },

  completeBody: {
    maxWidth: 330,
    margin: '10px 0 0',
    color: 'rgba(244,241,234,0.72)',
    fontSize: 15,
    lineHeight: 1.45,
    fontWeight: font.weight.semibold,
  },

  replayButton: {
    marginTop: 20,
    padding: '11px 18px',
    borderRadius: radius.pill,
    border: `1.5px solid ${CREAM}`,
    background: 'transparent',
    color: CREAM,
    fontSize: 14,
    fontWeight: font.weight.bold,
    cursor: 'pointer',
  },

  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
};
